/**
 * VMs Page Logic
 *
 * Virtual machines list and management
 */

import { api } from '../api/client.js';
import { apiCall } from '../api/helpers.js';
import { connectEvents, type EventsConnection } from '../api/events-ws.js';
import type { WsVmStatus } from '../api/types.js';
import { initAuthenticatedPage } from '../app/init.js';
import { initVmTable, updateVmInTable, stopUptimeTimer, setVisibleColumns } from '../vm/table.js';
import { vms, setVms } from '../globals.js';
import { renderVmTable } from '../vm/table.js';
import { escapeHtml } from '../utils.js';
import { vmModifyOrCreate } from '../vm/modify.js';
import { declareLoading, declareLoaded, confirmDialog } from '../ui/dialogs.js';

/**
 * Start all VMs
 */
export async function startAllVms(): Promise<void> {
  confirmDialog('Start all VMs?', async () => {
    declareLoading();
    await apiCall(api.startAllVms(), 'Failed to Start All VMs');
    declareLoaded();
  });
}

/**
 * Stop all VMs
 */
export async function stopAllVms(): Promise<void> {
  confirmDialog('Stop all VMs? This will gracefully shut down all running VMs.', async () => {
    declareLoading();
    await apiCall(api.stopAllVms(), 'Failed to Stop All VMs');
    declareLoaded();
  });
}

/**
 * Initialize the VMs page
 */
export async function initVmsPage(): Promise<void> {
  declareLoading();

  const user = await initAuthenticatedPage();
  if (!user) return; // Will redirect to login

  // Apply user's column visibility preferences before rendering
  // (Name, Description, and Actions are always shown — not toggleable)
  const defaultCols = ['ip_address', 'rdp_users'];
  const saved = user.settings?.vm_columns as string[] | undefined;
  const vmColumns = saved?.length ? saved : defaultCols;
  setVisibleColumns(vmColumns);

  // Fetch schema + VM list, render table
  await initVmTable();

  // Show page once the snapshot has arrived (fallback: 5s timeout)
  const loadedTimeout = setTimeout(declareLoaded, 5000);

  // One socket for every VM (local and delegated, merged by this edge) and
  // for the reachability of the hosts they run on (#186).
  events = connectEvents(['vm', 'host', 'task'], {
    onVm: (uuid, state, removed) => {
      // updateVmInTable deletes a field sent as null.
      const gone = Object.fromEntries(removed.map(f => [f, null]));
      updateVmInTable({ ...gone, ...state, type: 'vm_status', uuid } as unknown as WsVmStatus);
    },
    onVmRemoved: (uuid) => {
      const idx = vms.findIndex(vm => vm.uuid === uuid);
      if (idx >= 0) {
        vms.splice(idx, 1);
        setVms(vms);
        renderVmTable(vms);
      }
    },
    onHost: (host, state) => {
      hostStates.set(host, state);
      renderHostBanner();
    },
    onReady: () => { clearTimeout(loadedTimeout); declareLoaded(); },
  });
}

let events: EventsConnection | null = null;
const hostStates = new Map<string, Record<string, unknown>>();

/** One line per host this edge can't currently reach. */
function renderHostBanner(): void {
  let $banner = $('#host-banner');
  if ($banner.length === 0) {
    $('#vms').before('<div id="host-banner" role="status"></div>');
    $banner = $('#host-banner');
  }
  const lines: string[] = [];
  for (const [host, s] of hostStates) {
    const state = s['state'] as string;
    if (state === 'live' || state === 'connecting') continue;
    const since = typeof s['since'] === 'number'
      ? new Date((s['since'] as number) * 1000).toLocaleString() : '';
    const why = s['reason'] ? `: ${s['reason'] as string}` : '';
    lines.push(`Host <strong>${escapeHtml(host)}</strong> is ${escapeHtml(state)} since ${escapeHtml(since)}${escapeHtml(why)}`);
  }
  $banner.html(lines.join('<br/>')).toggle(lines.length > 0);
}

/**
 * Clean up when leaving the page
 */
export function cleanupVmsPage(): void {
  stopUptimeTimer();
  events?.close();
  events = null;
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).startAllVms = startAllVms;
  (window as unknown as Record<string, unknown>).stopAllVms = stopAllVms;
  (window as unknown as Record<string, unknown>).vmModifyOrCreate = vmModifyOrCreate;
  (window as unknown as Record<string, unknown>).initVmsPage = initVmsPage;
  (window as unknown as Record<string, unknown>).cleanupVmsPage = cleanupVmsPage;

  // Clean up on page unload
  window.addEventListener('beforeunload', cleanupVmsPage);
}
